// Third-party
import React from 'react';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import { SortingRule } from 'react-table';

// Components
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import DataTable from '../data/DataTable';
import { ErrorBoundary } from '../errors';
import { SearchInput } from '../inputs';
import { IconButton, PrimaryButton } from '../buttons';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { uniqueArray } from '../../../utils/objects';
import { CONTACT_LAYOUT, CONTACT_CATEGORIES } from '../../../utils/contacts';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../utils/permissions';
import { LoadingSpinner } from '../common';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import localStorageUtils from '@/utils/local-storage';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

// Types
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { SortOrder, SortingField } from '../../../types/data';
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';

export enum LegacyContactsTableFields {
  Uid = 'UID',
  Fname = 'FIRST_NAME',
  Lname = 'LAST_NAME',
  Email = 'EMAIL',
  Category = 'CATEGORY',
  Stage = 'STAGE',
  Id = 'ID',
}

type Props = {
  contacts: GenericContact[];
  className?: string;
  title?: string;
  searchable?: boolean;
  searchQuery?: string;
  selectable?: boolean;
  multiselect?: boolean;
  selectOnRowClick?: boolean;
  showCheckboxes: boolean;
  hasMore?: boolean;
  user?: User | null;
  layout?: CONTACT_LAYOUT;
  tableScrollAncestor?: Window | HTMLElement | string;
  loading?: boolean;
  isThemeLight?: boolean;
  fields?: LegacyContactsTableFields[];
  persistenceSource?: string;
  isPostMessageActive: boolean;
  showDataSourceTabs?: boolean;
  debouncedSearchContacts: () => void;
  onEdit?: (contact: GenericContact) => void;
  onDelete?: (contacts: GenericContact[]) => void;
  onClick: (contact: GenericContact) => void;
  onContactsSearch: (query: string) => void;
  onSortingChange: (rules: SortingField[]) => void;
  onLoadMore: () => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type State = {
  activeDataSource: string;
  selectedContacts: GenericContact[];
};

class ContactsTable extends React.Component<Props, State> {
  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.CONTACTS,
    this.props.isPostMessageActive,
  );

  static defaultProps = {
    showCheckboxes: true,
    searchable: true,
    selectable: true,
    searchQuery: '',
    onClick: () => {},
    onContactsSearch: () => {},
    layout: CONTACT_LAYOUT.CONTACT,
    showDataSourceTabs: true,
  };

  readonly state: State = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    selectedContacts: [],
  };

  componentDidMount() {
    const { isPostMessageActive, showDataSourceTabs, user, debouncedSearchContacts, setIsActiveContactPostMessage } = this.props;

    if (user && showDataSourceTabs) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActiveContactPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, user, () => debouncedSearchContacts());
    }
  }

  public clearSelection = () => {
    this.setState({ selectedContacts: [] });
  };

  onDeleteContacts = (contacts: GenericContact[]) => {
    const { onDelete } = this.props;
    if (onDelete) {
      onDelete(contacts);
    }
  };

  getTableActions = () => {
    const { onDelete, user } = this.props;
    const { selectedContacts } = this.state;

    const actions = [];

    const allContactsCanBeDeleted = selectedContacts.every(contact => contact.availableActions == null || contact.availableActions.delete);

    if (onDelete && user && userHasPermission(user, PERMISSIONS.CONTACTS_DELETE) && allContactsCanBeDeleted) {
      actions.push(
        <PrimaryButton key="action-delete" compact icon="ds-delete" onClick={() => this.onDeleteContacts(selectedContacts)}>
          <FormattedMessage id="Contacts.Delete" />
        </PrimaryButton>,
      );
    }

    return actions;
  };

  getTableColumns = () => {
    const { onEdit, user, layout, isThemeLight, fields } = this.props;
    const { selectedContacts } = this.state;
    const headerClassName = isThemeLight ? 'sortable' : '';

    const canEdit = onEdit != null && user != null && userHasPermission(user, PERMISSIONS.CONTACTS_EDIT);

    const columns: any[] = [];

    columns.push({
      Header: () => <span className={headerClassName}>{formatMessage(`Contacts.Fields.${layout === CONTACT_LAYOUT.CLIENT ? 'ClientID' : 'ContactID'}`)}</span>,
      field: LegacyContactsTableFields.Uid,
      accessor: 'uid',
      className: 'contact-uid',
      Cell: ({ value }: { value: string }) =>
        value ? (
          <span className="tag" data-private data-dd-privacy="mask">
            {value}
          </span>
        ) : null,
      width: 200,
    });

    columns.push({
      Header: () => <span className={headerClassName}>{formatMessage('Contacts.Fields.FirstName')}</span>,
      field: LegacyContactsTableFields.Fname,
      accessor: 'fname',
      Cell: ({ value }: { value: string }) =>
        value ? (
          <span data-private data-dd-privacy="mask">
            {value}
          </span>
        ) : null,
    });

    if (user && userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME)) {
      columns.push({
        Header: () => <span className={headerClassName}>{formatMessage('Contacts.Fields.LastName')}</span>,
        field: LegacyContactsTableFields.Lname,
        accessor: 'lname',
        Cell: ({ value }: { value: string }) =>
          value ? (
            <span data-private data-dd-privacy="mask">
              {value}
            </span>
          ) : null,
      });
    }

    if (user && userHasPermission(user, PERMISSIONS.CONTACTS_EMAIL)) {
      columns.push({
        Header: () => <span>{formatMessage('Contacts.Fields.Email')}</span>,
        field: LegacyContactsTableFields.Email,
        accessor: 'email',
        Cell: ({ value }: { value: string }) =>
          value ? (
            <span data-private data-dd-privacy="mask">
              {value}
            </span>
          ) : null,
      });
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.CONTACTS_CATEGORY)) {
      columns.push({
        Header: () => <span>{formatMessage('Contacts.Fields.Type')}</span>,
        field: LegacyContactsTableFields.Category,
        accessor: 'category',
        Cell: ({ value }: { value: CONTACT_CATEGORIES }) => (value ? <span>{formatMessage(`Contacts.Categories.${value}`)}</span> : null),
      });
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.CONTACTS_STAGE)) {
      columns.push({
        Header: () => <span>{formatMessage('Contacts.Fields.Stage')}</span>,
        field: LegacyContactsTableFields.Stage,
        accessor: 'stage',
        Cell: ({ value }: { value: string }) => (value ? <span>{formatMessage(`Contacts.ContactStages.${value}`)}</span> : null),
      });
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.CONTACTS_TYPE)) {
      columns.push({
        Header: () => <span>{formatMessage('Contacts.Fields.Type')}</span>,
        field: LegacyContactsTableFields.Category,
        accessor: 'contactType',
        Cell: ({ value }: { value: string }) => (value ? <span>{formatMessage(`Contacts.ContactTypes.${value}`)}</span> : null),
      });
    }

    if (canEdit) {
      columns.push({
        field: LegacyContactsTableFields.Id,
        accessor: '_id',
        className: 'action edit-contact',
        Cell: ({ original }: { original: GenericContact }) => {
          if (original.user == null && (original.availableActions == null || original.availableActions.edit)) {
            return <IconButton icon="pencil" onClick={() => onEdit(original)} disabled={selectedContacts.length > 0} />;
          }
          return null;
        },
        width: 48,
      });
    }

    return !fields ? columns : fields.map(field => columns.find(column => column.field === field)).filter(column => column != null);
  };

  onSelectContacts = (ids: string[]) => {
    const { contacts, selectable, multiselect } = this.props;
    const { selectedContacts } = this.state;

    if (!selectable) {
      return;
    }

    const selected = contacts.filter(contact => contact._id && ids.includes(contact._id));

    let newSelection;
    if (multiselect) {
      newSelection = uniqueArray(selectedContacts.concat(selected));
    } else {
      newSelection = selected;
    }

    this.setState({ selectedContacts: newSelection });
  };

  onUnselectContacts = (ids: string[]) => {
    const { selectable } = this.props;

    if (!selectable) {
      return;
    }

    this.setState(prevState => ({
      selectedContacts: prevState.selectedContacts.filter(c => c._id && !ids.includes(c._id)),
    }));
  };

  onContactClick = (id: string) => {
    const { contacts } = this.props;

    const contact = contacts.find(c => c._id === id);
    if (!contact) {
      return;
    }

    this.props.onClick(contact);
  };

  onSortingChange = (rules: SortingRule[]) => {
    this.props.onSortingChange(
      rules.map(rule => ({
        id: rule.id,
        order: rule.desc ? SortOrder.Desc : SortOrder.Asc,
      })),
    );
  };

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, debouncedSearchContacts, setIsActiveContactPostMessage, setPersistenceSource } = this.props;

    if (!user || !this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    debouncedSearchContacts();
  };

  render() {
    const { className, title, searchable, showDataSourceTabs, multiselect, selectable, selectOnRowClick, showCheckboxes, layout, searchQuery, loading } =
      this.props;
    const { activeDataSource, selectedContacts } = this.state;

    const showHeader = !!title || searchable;
    const placeholder = layout === CONTACT_LAYOUT.CLIENT ? formatMessage('Contacts.Search.PlaceholderClient') : formatMessage('Contacts.Search.Placeholder');

    return (
      <div className={classNames('contacts-table d-flex flex-column', className)}>
        <ErrorBoundary>
          {showHeader && (
            <div className="contacts-header d-flex align-items-start">
              <div className="flex-fill" data-private data-dd-privacy="mask">
                {title && <h1>{title}</h1>}
              </div>

              {searchable && (
                <SearchInput
                  className="contact-search"
                  placeholder={placeholder}
                  onChange={this.props.onContactsSearch}
                  value={searchQuery}
                  data-private
                  data-dd-privacy="mask"
                />
              )}
            </div>
          )}

          {loading && <LoadingSpinner message={formatMessage('Contacts.Loading')} />}
          {this.dataSourceTabs && showDataSourceTabs && (
            <div className="ms-4 mt-2">
              <DataSourceTabs disabled={loading} tabs={this.dataSourceTabs} value={activeDataSource} onChange={this.onSelectDataSource} />
            </div>
          )}
          <DataTable
            singleSelection
            data-private
            data-dd-privacy="mask"
            sortable
            serverSideSorting
            className={classNames({ hidden: loading })}
            selectOnRowClick={selectable && selectOnRowClick}
            canSelect={selectable && showCheckboxes}
            columns={this.getTableColumns()}
            selectedActions={this.getTableActions()}
            data={this.props.contacts}
            selected={selectedContacts}
            noDataText={formatMessage('ContactsTable.NoRows')}
            onSelectItems={this.onSelectContacts}
            onUnselectItems={this.onUnselectContacts}
            onRowClick={this.onContactClick}
            selectedTitle={!multiselect ? undefined : formatMessage('Contacts.SelectedTitle', { count: selectedContacts.length })}
            onSortingChange={this.onSortingChange}
            defaultSortDesc={false}
            onLoadMore={this.props.onLoadMore}
            showLoadMore={this.props.hasMore}
            scrollAncestor={this.props.tableScrollAncestor}
          />
        </ErrorBoundary>
      </div>
    );
  }
}

export default ContactsTable;
